import { z } from 'zod';

import { ZONES, type Holiday, type HolidayCreateInput, type HolidayUpdateInput } from './api';

/** `''` maps to a global holiday (`zone: null`) — the explicit "Todas las zonas" option (`holidays/spec.md` → "Tabla muestra 'Todas las zonas' para feriado global"). */
export const HOLIDAY_FORM_GLOBAL_ZONE = '';

/**
 * Mirrors `apps/api` `createHolidaySchema`/`updateHolidaySchema`
 * (`holiday.dto.ts`). `zone`/`date` stay in the schema so the edit form
 * can render them read-only, but `toHolidayUpdateInput` never sends them
 * (`holidays/spec.md` → "Edición de feriado").
 */
export const holidayFormSchema = z.object({
  zone: z.union([z.enum(ZONES), z.literal(HOLIDAY_FORM_GLOBAL_ZONE)]),
  date: z.string().trim().min(1, 'La fecha es obligatoria'),
  name: z.string().trim().min(1, 'El nombre es obligatorio').max(200),
  hasService: z.boolean(),
  description: z.string().trim().max(500),
});

export type HolidayFormValues = z.infer<typeof holidayFormSchema>;

export const DEFAULT_HOLIDAY_FORM_VALUES: HolidayFormValues = {
  zone: HOLIDAY_FORM_GLOBAL_ZONE,
  date: '',
  name: '',
  hasService: false,
  description: '',
};

export function toHolidayFormValues(holiday: Holiday): HolidayFormValues {
  return {
    zone: holiday.zone ?? HOLIDAY_FORM_GLOBAL_ZONE,
    date: holiday.date,
    name: holiday.name,
    hasService: holiday.hasService,
    description: holiday.description ?? '',
  };
}

export function toHolidayCreateInput(values: HolidayFormValues): HolidayCreateInput {
  return {
    zone: values.zone === HOLIDAY_FORM_GLOBAL_ZONE ? null : values.zone,
    date: values.date,
    name: values.name,
    hasService: values.hasService,
    description: values.description === '' ? null : values.description,
  };
}

export function toHolidayUpdateInput(values: HolidayFormValues): HolidayUpdateInput {
  return {
    name: values.name,
    hasService: values.hasService,
    description: values.description === '' ? null : values.description,
  };
}
